'use client'

import { useState } from 'react'
import Image from 'next/image'
import Modal from 'react-modal'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import { AudioPlayer as InlineAudioPlayer } from 'src/components/AudioPlayer'
import { VideoPlayer as InlineVideoPlayer } from 'src/components/VideoPlayer'
import { VideoPoster } from 'src/components/VideoPoster'
import { getMediaUrl } from 'src/_helpers/client/getMediaUrl'
import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'
import Typography from 'src/components/design-system/Typography'
import DOMPurify from 'src/_helpers/dompurify'
import TranscriptionIcon from 'public/icons/interface/transcription.svg'
import AudioDescIcon from 'public/icons/interface/audiodesc.svg'
import PjmIcon from 'public/icons/interface/pjm.svg'

export default function Abstract({ page, locale, id = 'abstract' }) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [activeModal, setActiveModal] = useState(null)
  const translateEntry = useTranslateEntry()
  const translation = getPreferredTranslation(page, locale)
  const abstractTranslation = getPreferredTranslation(page, locale, { preferredFields: ['abstract'] })
  const text = abstractTranslation?.abstract || null
  const imgTitle = translation?.abstractPhotoTitle || ''
  const thumb = page?.thumbs?.abstract || null
  const thumbUrl = thumb ? getMediaUrl(thumb.url, thumb.version) : null
  const youtubePoster = page?.abstractVideoYoutube
    ? `https://img.youtube.com/vi/${page.abstractVideoYoutube}/maxresdefault.jpg`
    : null
  const posterUrl = thumbUrl || youtubePoster || null
  const alt = imgTitle || translateEntry('T_ABSTRACT')
  const transcription = translation?.abstractTranscription
    ? DOMPurify.sanitize(translation.abstractTranscription)
    : null
  const videoSrc = page?.abstractVideoYoutube
    ? `https://www.youtube.com/watch?v=${page.abstractVideoYoutube}`
    : page?.abstractVideoFile?.url
      ? getMediaUrl(page?.abstractVideoFile?.url)
      : null
  const audioDescriptionUrl = page?.abstractAudioDescriptionFile?.url
    ? getMediaUrl(page?.abstractAudioDescriptionFile?.url)
    : null
  const signLanguageSrc = page?.abstractPslFile?.url ? getMediaUrl(page?.abstractPslFile?.url) : null
  const featureBadges = [
    transcription && {
      key: 'transcription',
      label: translateEntry('T_TRANSCRIPTION'),
      icon: TranscriptionIcon,
      onClick: () => setActiveModal('transcription'),
      ariaLabel: translateEntry('T_OPEN_TRANSCRIPTION'),
    },
    audioDescriptionUrl && {
      key: 'audioDescription',
      label: translateEntry('T_AUDIOESCRIPTION'),
      icon: AudioDescIcon,
      onClick: () => setActiveModal('audio'),
      ariaLabel: translateEntry('T_OPEN_AUDIO_DESCRIPTION'),
    },
    signLanguageSrc && {
      key: 'signLanguage',
      label: translateEntry('T_SIGN_LANGUAGE'),
      icon: PjmIcon,
      onClick: () => setActiveModal('sign'),
      ariaLabel: translateEntry('T_OPEN_SIGN_LANGUAGE'),
    },
  ].filter(Boolean)

  const handlePlay = () => {
    if (!videoSrc) return
    setIsPlaying(true)
  }
  return text || videoSrc || thumbUrl ? (
    <section id={id}>
      <div className="flex flex-col gap-y-23 my-64 d:max-w-[777px] w-full">
        <Typography variant="h2" component="h2" color="text-basic-black">
          {translateEntry('T_ABSTRACT')}
        </Typography>
        <div>
          {videoSrc ? (
            <>
              <div className="relative w-full overflow-hidden min-h-190 d:min-h-469">
                {isPlaying && videoSrc ? (
                  <InlineVideoPlayer src={videoSrc} title={translation?.title} variant="abstract" autoplay />
                ) : (
                  <VideoPoster
                    src={posterUrl}
                    alt={alt}
                    width={thumb?.width || 1359}
                    height={thumb?.height || 640}
                    imageClassName="h-full min-h-[inherit] w-full object-cover aspect-[776/469]"
                    onPlay={videoSrc ? handlePlay : null}
                  />
                )}
              </div>
              {featureBadges.length > 0 && (
                <div className="flex flex-wrap gap-16 mt-12">
                  {featureBadges.map((feature) => (
                    <button
                      key={feature.key}
                      onClick={feature.onClick}
                      className="inline-flex cursor-pointer items-center gap-6 px-10 py-4 text-left"
                      aria-haspopup="dialog"
                      aria-label={feature.ariaLabel}
                    >
                      <feature.icon aria-hidden="true" />
                      <Typography variant="button-l-bold" color="text-dark-blue-dark-blue2" className="max-t:hidden">
                        {feature.label}
                      </Typography>
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : posterUrl ? (
            <figure className="w-full flex flex-col gap-y-8">
              <Image className="w-full h-auto" src={posterUrl} alt={alt} width={thumb.width} height={thumb.height} />
              {imgTitle && (
                <figcaption>
                  <Typography
                    variant="body3"
                    component="span"
                    color="text-dark-blue-dark-blue1"
                    className="flex gap-x-10 items-center before:page before:w-12 before:h-12 before:bg-style4 before:rounded-full"
                  >
                    {imgTitle}
                  </Typography>
                </figcaption>
              )}
            </figure>
          ) : null}
        </div>

        {text && (
          <Typography variant="body2" component="p" color="text-basic-black">
            {text}
          </Typography>
        )}
      </div>

      {[
        {
          key: 'transcription',
          isOpen: activeModal === 'transcription',
          onClose: () => setActiveModal(null),
          contentLabel: translateEntry('T_TRANSCRIPTION_MODAL', true),
          render: () =>
            transcription && (
              <div
                className="text-block-content body-b2 text-dark-blue-dark-blue1"
                dangerouslySetInnerHTML={{ __html: transcription }}
              />
            ),
        },
        {
          key: 'audio',
          isOpen: activeModal === 'audio',
          onClose: () => setActiveModal(null),
          contentLabel: translateEntry('T_AUDIO_DESC_MODAL', true),
          render: () =>
            audioDescriptionUrl && (
              <div className="w-full max-w-4xl overflow-hidden rounded-1 bg-style3 shadow-[0_4px_14px_rgba(0,0,0,0.08)]">
                <InlineAudioPlayer
                  audio={audioDescriptionUrl}
                  title={translateEntry('T_AUDIOESCRIPTION')}
                  className="max-w-none"
                  style={{
                    '--audio-bg': 'transparent',
                    '--audio-border': 'none',
                    '--audio-filter': 'none',
                    '--audio-controls-color': 'var(--c-dark-blue1)',
                    '--audio-slider-track-bg': 'var(--c-dark-blue6)',
                    '--audio-slider-progress-bg': 'var(--c-dark-blue1)',
                    '--audio-button-size': '48px',
                    '--media-focus-ring-color': 'var(--c-focus-color)',
                  }}
                />
              </div>
            ),
        },
        {
          key: 'sign',
          isOpen: activeModal === 'sign',
          onClose: () => setActiveModal(null),
          contentLabel: translateEntry('T_PJM_MODAL', true),
          render: () =>
            signLanguageSrc && <InlineVideoPlayer src={signLanguageSrc} title={translation?.title} autoplay />,
        },
      ].map(
        (modal) =>
          modal.render && (
            <Modal
              key={modal.key}
              isOpen={modal.isOpen}
              onRequestClose={modal.onClose}
              contentLabel={modal.contentLabel}
              className="fixed left-1/2 top-1/2 w-[min(95vw,960px)] -translate-x-1/2 -translate-y-1/2 bg-basic-white p-24 outline-none"
              overlayClassName="fixed inset-0 bg-black/40 z-50 flex items-center justify-center"
            >
              {modal.render()}
            </Modal>
          ),
      )}
    </section>
  ) : null
}
